import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useSalesSummary } from '@/hooks/use-sales-summary';
import {
  AlertCircle,
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Package,
  CreditCard,
  Tag,
  Receipt,
  FileText,
  RotateCcw,
  Ban,
  CheckCircle2,
  Clock,
  Percent,
} from 'lucide-react';

interface SaleReportTabProps {
  fromDate: string;
  toDate: string;
}

interface PaymentBreakdown {
  byMethod?: Record<string, number>;
  byStatus?: Record<string, number>;
  cashOnDeliveryAmount?: number | null;
  prepaidAmount?: number | null;
}

interface SalesSummary {
  metrics: {
    totalRevenue: number;
    netRevenue: number;
    totalOrders: number;
    confirmedOrders: number;
    paidOrders: number;
    cancelledOrders: number;
    refundedOrders: number;
    returnedOrders?: number;
    totalItemsSold: number;
    averageOrderValue: number;
    totalDeliveryFees: number;
    totalDiscounts: number;
    totalRefunds: number;
    totalPackagingCost: number;
    totalTax: number;
  };
  paymentBreakdown: PaymentBreakdown;
}

export function SaleReportTab({ fromDate, toDate }: SaleReportTabProps) {
  const {
    data: salesSummary,
    isLoading: isLoadingSales,
    error: salesError,
  } = useSalesSummary(fromDate, toDate);

  if (isLoadingSales) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-3 w-40" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (salesError) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex flex-col items-center justify-center text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">Error loading sales data</h3>
            <p className="text-muted-foreground">
              {salesError instanceof Error
                ? salesError.message
                : 'Failed to load sales summary'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!salesSummary) {
    return null;
  }

  const formatCurrency = (value: number) =>
    value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <div className="space-y-6">
      {/* Primary KPIs - Most Important Metrics */}
      <div className="grid gap-4 lg:grid-cols-4 md:grid-cols-2">
        {/* Net Revenue - Hero Metric */}
        <Card className="relative overflow-hidden border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-50/80 via-white to-white dark:from-emerald-950/20 dark:via-background dark:to-background">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -mr-16 -mt-16" />
          <CardHeader className="pb-3 relative">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <b>Net Revenue</b> (Paid Order)
                </CardTitle>
                <div className="text-3xl font-bold text-emerald-700 dark:text-emerald-400 tracking-tight">
                  ${formatCurrency(salesSummary.metrics.netRevenue)}
                </div>
              </div>
              <div className="p-2.5 bg-emerald-100 dark:bg-emerald-950/50 rounded-lg">
                <DollarSign className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-400 dark:border-emerald-800">
                {salesSummary.metrics.paidOrders} paid
              </Badge>
              <span className="text-xs text-muted-foreground">orders</span>
            </div>
          </CardContent>
        </Card>

        {/* Total Orders */}
        <Card className="relative overflow-hidden border-l-4 border-l-blue-500 bg-gradient-to-br from-blue-50/80 via-white to-white dark:from-blue-950/20 dark:via-background dark:to-background">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full -mr-16 -mt-16" />
          <CardHeader className="pb-3 relative">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Total Orders
                </CardTitle>
                <div className="text-3xl font-bold text-blue-700 dark:text-blue-400 tracking-tight">
                  {salesSummary.metrics.totalOrders}
                </div>
              </div>
              <div className="p-2.5 bg-blue-100 dark:bg-blue-950/50 rounded-lg">
                <ShoppingCart className="h-5 w-5 text-blue-700 dark:text-blue-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-400 dark:border-blue-800">
                {salesSummary.metrics.confirmedOrders} confirmed
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Total Items */}
        <Card className="relative overflow-hidden border-l-4 border-l-violet-500 bg-gradient-to-br from-violet-50/80 via-white to-white dark:from-violet-950/20 dark:via-background dark:to-background">
          <div className="absolute top-0 right-0 w-32 h-32 bg-violet-500/5 rounded-full -mr-16 -mt-16" />
          <CardHeader className="pb-3 relative">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Items Sold
                </CardTitle>
                <div className="text-3xl font-bold text-violet-700 dark:text-violet-400 tracking-tight">
                  {salesSummary.metrics.totalItemsSold}
                </div>
              </div>
              <div className="p-2.5 bg-violet-100 dark:bg-violet-950/50 rounded-lg">
                <Package className="h-5 w-5 text-violet-700 dark:text-violet-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {salesSummary.metrics.totalOrders > 0
                  ? `${(salesSummary.metrics.totalItemsSold / salesSummary.metrics.totalOrders).toFixed(1)} items/order`
                  : 'No orders'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Total Revenue */}
        <Card className="relative overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Total Revenue
                </CardTitle>
                <div className="text-3xl font-bold tracking-tight">
                  ${formatCurrency(salesSummary.metrics.totalRevenue)}
                </div>
              </div>
              <div className="p-2.5 bg-muted rounded-lg">
                <TrendingUp className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Gross sales</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Order Status & Payment Methods */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Order Status Breakdown */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">Order Status Breakdown</CardTitle>
              <Receipt className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Confirmed Orders */}
              <div className="flex items-center justify-between p-3 rounded-lg border bg-gradient-to-r from-green-50/50 to-transparent dark:from-green-950/10">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 dark:bg-green-950/50 rounded-md">
                    <CheckCircle2 className="h-4 w-4 text-green-700 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Confirmed Orders</p>
                    <p className="text-xs text-muted-foreground">Ready for processing</p>
                  </div>
                </div>
                <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                  {salesSummary.metrics.confirmedOrders}
                </div>
              </div>

              {/* Paid Orders */}
              <div className="flex items-center justify-between p-3 rounded-lg border bg-gradient-to-r from-blue-50/50 to-transparent dark:from-blue-950/10">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-950/50 rounded-md">
                    <CreditCard className="h-4 w-4 text-blue-700 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Paid Orders</p>
                    <p className="text-xs text-muted-foreground">Payment received</p>
                  </div>
                </div>
                <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                  {salesSummary.metrics.paidOrders}
                </div>
              </div>

              {/* Cancelled Orders */}
              {salesSummary.metrics.cancelledOrders > 0 && (
                <div className="flex items-center justify-between p-3 rounded-lg border bg-gradient-to-r from-red-50/50 to-transparent dark:from-red-950/10">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 dark:bg-red-950/50 rounded-md">
                      <Ban className="h-4 w-4 text-red-700 dark:text-red-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Cancelled Orders</p>
                      <p className="text-xs text-muted-foreground">Order cancelled</p>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-red-700 dark:text-red-400">
                    {salesSummary.metrics.cancelledOrders}
                  </div>
                </div>
              )}

              {/* Refunded Orders */}
              {salesSummary.metrics.refundedOrders > 0 && (
                <div className="flex items-center justify-between p-3 rounded-lg border bg-gradient-to-r from-orange-50/50 to-transparent dark:from-orange-950/10">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 dark:bg-orange-950/50 rounded-md">
                      <RotateCcw className="h-4 w-4 text-orange-700 dark:text-orange-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Refunded Orders</p>
                      <p className="text-xs text-muted-foreground">Money returned</p>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-orange-700 dark:text-orange-400">
                    {salesSummary.metrics.refundedOrders}
                  </div>
                </div>
              )}

              {/* Returned Orders */}
              {salesSummary.metrics.returnedOrders !== undefined &&
                salesSummary.metrics.returnedOrders > 0 && (
                  <div className="flex items-center justify-between p-3 rounded-lg border bg-gradient-to-r from-amber-50/50 to-transparent dark:from-amber-950/10">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 dark:bg-amber-950/50 rounded-md">
                        <Package className="h-4 w-4 text-amber-700 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Returned Orders</p>
                        <p className="text-xs text-muted-foreground">Product returned</p>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">
                      {salesSummary.metrics.returnedOrders}
                    </div>
                  </div>
                )}
            </div>
          </CardContent>
        </Card>

        {/* Payment Methods */}
        <Card className="border-l-4 border-l-indigo-500">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">Payment Methods</CardTitle>
              <CreditCard className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Payment by Method */}
              {salesSummary.paymentBreakdown?.byMethod &&
                Object.keys(salesSummary.paymentBreakdown.byMethod).length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-muted-foreground">By Method</h4>
                    {Object.entries(salesSummary.paymentBreakdown.byMethod).map(([method, amount]) => (
                      <div
                        key={method}
                        className="flex items-center justify-between p-4 rounded-lg border-2 bg-gradient-to-r from-indigo-50/50 to-transparent dark:from-indigo-950/10 hover:border-indigo-200 dark:hover:border-indigo-800 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-indigo-100 dark:bg-indigo-950/50 rounded-md">
                            <CreditCard className="h-4 w-4 text-indigo-700 dark:text-indigo-400" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold">{method}</p>
                            <p className="text-xs text-muted-foreground">Payment method</p>
                          </div>
                        </div>
                        <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-400">
                          ${formatCurrency(amount as number)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              {/* Payment Status */}
              {salesSummary.paymentBreakdown?.byStatus &&
                Object.keys(salesSummary.paymentBreakdown.byStatus).length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-muted-foreground">By Status</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {Object.entries(salesSummary.paymentBreakdown.byStatus).map(([status, count]) => (
                          <div
                            key={status}
                            className="p-3 rounded-lg border bg-muted/30"
                          >
                            <div className="flex items-center gap-2 mb-2">
                              {status === 'PAID' ? (
                                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                              ) : (
                                <Clock className="h-3.5 w-3.5 text-yellow-600" />
                              )}
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                {status}
                              </p>
                            </div>
                            <p className="text-2xl font-bold">{count}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

              {/* Fallback for COD and Prepaid */}
              {!salesSummary.paymentBreakdown?.byMethod &&
                (salesSummary.paymentBreakdown?.cashOnDeliveryAmount !== null ||
                  salesSummary.paymentBreakdown?.prepaidAmount !== null) && (
                  <div className="space-y-3">
                    {salesSummary.paymentBreakdown.cashOnDeliveryAmount !== null &&
                      salesSummary.paymentBreakdown.cashOnDeliveryAmount !== undefined && (
                        <div className="flex items-center justify-between p-4 rounded-lg border-2 bg-gradient-to-r from-indigo-50/50 to-transparent dark:from-indigo-950/10">
                          <div>
                            <p className="text-sm font-semibold">Cash on Delivery</p>
                            <p className="text-xs text-muted-foreground">Payment method</p>
                          </div>
                          <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-400">
                            ${formatCurrency(salesSummary.paymentBreakdown.cashOnDeliveryAmount)}
                          </div>
                        </div>
                      )}
                    {salesSummary.paymentBreakdown.prepaidAmount !== null &&
                      salesSummary.paymentBreakdown.prepaidAmount !== undefined && (
                        <div className="flex items-center justify-between p-4 rounded-lg border-2 bg-gradient-to-r from-indigo-50/50 to-transparent dark:from-indigo-950/10">
                          <div>
                            <p className="text-sm font-semibold">Prepaid</p>
                            <p className="text-xs text-muted-foreground">Payment method</p>
                          </div>
                          <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-400">
                            ${formatCurrency(salesSummary.paymentBreakdown.prepaidAmount)}
                          </div>
                        </div>
                      )}
                  </div>
                )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Financial Metrics & Additional Details */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Revenue Details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Revenue Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-emerald-500" />
                  <span className="text-sm text-muted-foreground">Net Revenue</span>
                </div>
                <span className="text-base font-bold text-emerald-600">
                  ${formatCurrency(salesSummary.metrics.netRevenue)}
                </span>
              </div>
              
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-blue-500" />
                  <span className="text-sm text-muted-foreground">Total Revenue</span>
                </div>
                <span className="text-base font-semibold">
                  ${formatCurrency(salesSummary.metrics.totalRevenue)}
                </span>
              </div>

              <Separator />

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <Package className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Delivery Fees</span>
                </div>
                <span className="text-base font-semibold text-green-600">
                  +${formatCurrency(salesSummary.metrics.totalDeliveryFees)}
                </span>
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Avg Order Value</span>
                </div>
                <span className="text-base font-semibold">
                  ${formatCurrency(salesSummary.metrics.averageOrderValue)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Deductions & Costs */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Deductions & Costs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Discounts</span>
                </div>
                <span className="text-base font-semibold text-red-600">
                  {salesSummary.metrics.totalDiscounts > 0 ? '-' : ''}$
                  {formatCurrency(salesSummary.metrics.totalDiscounts)}
                </span>
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <RotateCcw className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Refunds</span>
                </div>
                <span className="text-base font-semibold text-red-600">
                  {salesSummary.metrics.totalRefunds > 0 ? '-' : ''}$
                  {formatCurrency(salesSummary.metrics.totalRefunds)}
                </span>
              </div>

              <Separator />

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <Package className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Packaging</span>
                </div>
                <span className="text-base font-semibold">
                  ${formatCurrency(salesSummary.metrics.totalPackagingCost)}
                </span>
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <Percent className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Tax</span>
                </div>
                <span className="text-base font-semibold">
                  ${formatCurrency(salesSummary.metrics.totalTax)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Performance Metrics */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Performance Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground mb-1">Total Items</p>
                <p className="text-2xl font-bold">{salesSummary.metrics.totalItemsSold}</p>
              </div>

              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground mb-1">Items per Order</p>
                <p className="text-2xl font-bold">
                  {salesSummary.metrics.totalOrders > 0
                    ? (salesSummary.metrics.totalItemsSold / salesSummary.metrics.totalOrders).toFixed(
                        1,
                      )
                    : '0.0'}
                </p>
              </div>

              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground mb-1">Avg Order Value</p>
                <p className="text-2xl font-bold">
                  ${formatCurrency(salesSummary.metrics.averageOrderValue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}